import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Flex } from "@radix-ui/themes";
import { params } from "@/params";
import { ChatSettings, QuackChatConfigResponse } from "./QuackChatConfig.model";
import { Message } from "./core/entities/Message";
import { QuackChatComp } from "./core/parts/QuackChatComp";
import { QuackChatWidgetTheme } from "./core/parts/QuackChatWidgetTheme";
import { TriggerSVG } from "./core/parts/QuackWidgetContainer";
import { WalkthroughProvider } from "./core/parts/WalkthroughContext";
import "./core/styles/index.scss";
import { SessionNotification as SessionNotificationType } from "./core/entities/SessionNotification";
import { SessionNotification } from "./core/parts/SessionNotification";

export const defaultTheme: ChatSettings["theme"] = {
  iconPrimary: "#1A1A1A",
  widgetPrimary: "#1A1A1A",
  background: "#FFFFFF",
  lightBorder: "#656665",
  textPlaceholder: "#8F8F8F",
  borderDefault: "#F1F1F1",
  bgCard: "#E6E6E6",
  bgInverted: "#1D1D1D",
  textPrimary: "#1A1A1A",
  textInverted: "#FFFFFF",
  borderLight: "#CBCBCB",
  bgPrimaryQuack: "#FFFFFF",
  bgSendButton: "#6F5BE2",
  textSendButton: "#FAFBFC",
  bgSecondaryButton: "#6F5BE2",
  textSecondaryButton: "#FAFBFC",
  bgChatWidget: "#ffffff00",
  bgInput: "#F0F0F0",
  agentBackground: "#6F5BE2",
  agentText: "#FAFBFC",
  userBackground: "#FFFFFF",
  userText: "#000000",
};

export const defaultChatSettings: ChatSettings = {
  name: "Quack demo's AI Agent",
  quackAgentName: "AI Assistant",
  customSVG:
    '<svg fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" shape-rendering="geometricPrecision" viewBox="0 0 24 24" class="jsx-3060654314" height="24" width="24" style="color: currentcolor;"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"></path></svg>',
  borderRadius: 8,
  iconSize: 48,
  iconPadding: 4,
  poweredByLine: false,
  escalationMessageToLiveAgent:
    "We understand that this inquiry requires the help of one of our Support Agents. Please hold while we connect you to one of our Agents.",
  escalationMessageToNotLiveAgent:
    "We understand that this inquiry requires the help of one of our Support Agents. At this time all of our agents are offline, so we will open a ticket to the team on your behalf. They will get back to you as soon as they can.",
  theme: defaultTheme,
  disappearAfterClose: 10,
  fontSize: 14,
  waitTimeBeforeRefreshAfterEscalating: 300,
  openFullyOnOpenChat: false,
  csatDisabled: false,
  csatType: "rating",
  allowCsatComment: false,
  csatCommentText: "Help us improve your experience",
  placeholder: "Ask anything...",
  enableNewMessageSound: false,
  enablePreChatFlow: false,
  preChatWelcomeMessage: "Welcome 👋",
  preChatWelcomeSubtitle: "Let's get started! Please fill in your details below.",
  preChatCaseTitle: "How can we help you today?",
  preChatAvatarRadius: "regular",
  minimizeIcon: "chevron",
};

interface QuackChatProps {
  config: QuackChatConfigResponse;
  agents?: string[];
  messages?: Message[];
  thinking?: boolean;
  showCSAT?: boolean;
  sessionNotification?: SessionNotificationType;
}

export function QuackChat({
  config,
  sessionNotification,
  ...props
}: QuackChatProps) {
  const { user } = useAuth0();

  const chatSettings = useMemo<ChatSettings>(() => {
    const { customSVG, closeSVG, avatarSVG, userAvatarSVG } =
      config.chatSettings;

    return {
      ...defaultChatSettings,
      ...config.chatSettings,
      customSVG: `${params.SDK_CONFIG}${customSVG}`,
      ...(avatarSVG != null
        ? { avatarSVG: `${params.SDK_CONFIG}${avatarSVG}` }
        : {}),
      ...(closeSVG != null
        ? { closeSVG: `${params.SDK_CONFIG}${closeSVG}` }
        : {}),
      ...(userAvatarSVG != null
        ? { userAvatarSVG: `${params.SDK_CONFIG}${userAvatarSVG}` }
        : {}),
    };
  }, [config.chatSettings]);

  const {
    customSVG,
    closeSVG = customSVG,
    iconSize,
    position = "bottom-right",
  } = chatSettings;

  return (
    <Box>
      <WalkthroughProvider>
        <QuackChatWidgetTheme themeVars={chatSettings.theme}>
          <Flex
            direction={"column"}
            align={position === "bottom-right" ? "end" : "start"}
            gap={"2"}
          >
            <Box className={"qc-Chat__Widget_Popover"}>
              {sessionNotification != null && (
                <SessionNotification
                  settings={chatSettings}
                  notification={sessionNotification}
                  onAcknowledge={() => {}}
                />
              )}
              <QuackChatComp
                {...props}
                settings={chatSettings}
                tenant={user?.owner}
              />
            </Box>

            <button>
              <TriggerSVG
                open
                customSVG={customSVG}
                closeSVG={closeSVG}
                iconSize={iconSize}
                radius={chatSettings.svgRadius}
              />
            </button>
          </Flex>
        </QuackChatWidgetTheme>
      </WalkthroughProvider>
    </Box>
  );
}
